import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { addDays, format, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, Plus, X, MapPin, ArrowLeft } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAssignFlow } from "@/components/staffing/useAssignFlow";
import { NotifyState, NotifyButton, ConflictIcon, NotifyTeamButton } from "@/components/staffing/notify";
import { api } from "@/lib/api";
import { usePositions } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";
import { fmtDate, fmtRange } from "@/lib/format";

const Chip = ({ a, ev, onRemove, reload }) => {
  const { t } = useLang();
  return (
    <div draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify({ id: a.id, event_id: ev.id, slot_id: a.slot_id, name: a.employee_name }))}
      data-testid={`day-chip-${a.id}`}
      className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs cursor-grab active:cursor-grabbing bg-white/[0.03] ${a.conflicts?.length ? "border-red-400/60" : "border-white/[0.08]"}`}>
      <span className="font-medium truncate">{a.employee_name}</span>
      <span className="mono text-[10px] text-neutral-500 whitespace-nowrap">{fmtRange(a.start_time, a.end_time, t)}</span>
      <span className="flex-1" />
      <ConflictIcon a={a} />
      <NotifyState a={a} />
      <NotifyButton a={a} eventId={ev.id} reload={reload} />
      <button onClick={() => onRemove(a)} data-testid={`day-remove-${a.id}`} aria-label="remove" className="press p-0.5 text-neutral-400 hover:text-red-500"><X className="w-3 h-3" /></button>
    </div>
  );
};

const AddPerson = ({ slot, staff, onPick }) => {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const taken = slot.assignments.map((a) => a.employee_id);
  const qual = (s) => s.positions.includes(slot.position_key);
  const list = staff.filter((s) => !taken.includes(s.id) && s.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => qual(b) - qual(a) || a.name.localeCompare(b.name));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`day-add-${slot.id}`} className="press inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-[#D9B872]"><Plus className="w-3 h-3" />{t("day.add")}</button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 rounded-xl" data-testid={`day-add-popover-${slot.id}`}>
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("day.search")} className="h-8 text-xs mb-2" data-testid={`day-add-search-${slot.id}`} />
        <div className="max-h-60 overflow-y-auto">
          {list.map((s) => (
            <button key={s.id} data-testid={`day-add-option-${slot.id}-${s.id}`} onClick={() => { setOpen(false); onPick(s); }}
              className="w-full text-left px-2 py-1.5 rounded-md text-xs hover:bg-[#C5A059]/10 flex justify-between gap-2">
              <span className="truncate">{s.name}</span>{!qual(s) && <span className="text-neutral-500 text-[10px]">{t("day.notQualified")}</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SlotCell = ({ ev, slot, staff, onDrop, onAssign, onRemove, reload }) => {
  const { t } = useLang();
  const [hot, setHot] = useState(false);
  const short = Math.max(slot.required - slot.assignments.length, 0);
  return (
    <div data-testid={`day-slot-${slot.id}`} onDragOver={(e) => { e.preventDefault(); setHot(true); }} onDragLeave={() => setHot(false)}
      onDrop={(e) => { e.preventDefault(); setHot(false); try { onDrop(ev, slot, JSON.parse(e.dataTransfer.getData("text/plain"))); } catch { /* ignore */ } }}
      className={`rounded-xl border p-2 space-y-1.5 transition-colors ${hot ? "border-[#C5A059] bg-[#C5A059]/10" : "border-white/[0.06]"}`}>
      <div className="flex justify-between text-[10px]">
        <span className="mono text-neutral-500">{fmtRange(slot.start_time, slot.end_time, t)}</span>
        <span className={`mono ${short ? "text-red-400" : "text-emerald-500"}`}>{slot.assignments.length}/{slot.required}</span>
      </div>
      {slot.assignments.map((a) => <Chip key={a.id} a={a} ev={ev} onRemove={onRemove} reload={reload} />)}
      {short > 0 && <div className="text-[10px] text-red-400" data-testid={`day-short-${slot.id}`}>{short} {t("day.toFill")}</div>}
      <AddPerson slot={slot} staff={staff} onPick={(p) => onAssign(ev, slot, p)} />
    </div>
  );
};

const EventHead = ({ ev, reload }) => {
  const { t } = useLang();
  const count = ev.slots.reduce((n, s) => n + s.assignments.length, 0);
  return (
    <th className="align-top text-left p-3 min-w-[260px] border-b border-white/[0.08]" data-testid={`day-event-${ev.id}`}>
      <Link to={`/admin/events/${ev.id}`} className="font-display text-lg leading-tight hover:text-[#D9B872]">{ev.name}</Link>
      <div className="text-[11px] text-neutral-500 font-normal flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{ev.location || ev.address}</div>
      <div className="mono text-[11px] text-neutral-500 font-normal mt-0.5">{fmtRange(ev.start_time, ev.end_time, t)} · {ev.stats.filled}/{ev.stats.required}</div>
      <div className="mt-2 font-normal"><NotifyTeamButton eventId={ev.id} count={count} reload={reload} testid={`day-notify-team-${ev.id}`} /></div>
    </th>
  );
};

const MoveDialog = ({ pending, onClose, onChoose }) => {
  const { t } = useLang();
  return (
    <AlertDialog open={!!pending} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="rounded-2xl" data-testid="day-move-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">{t("day.moveTitle")}</AlertDialogTitle>
          <AlertDialogDescription><strong>{pending?.item.name}</strong> → {pending?.ev.name}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel className="rounded-full" data-testid="day-move-cancel">{t("c.cancel")}</AlertDialogCancel>
          <AlertDialogAction className="rounded-full border border-[#C5A059] bg-transparent text-[#D9B872] hover:bg-[#C5A059]/10" data-testid="day-move-keep" onClick={() => onChoose(true)}>{t("day.keep")}</AlertDialogAction>
          <AlertDialogAction className="rounded-full bg-[#C5A059] text-[#0A0A0C] hover:bg-[#D9B872]" data-testid="day-move-move" onClick={() => onChoose(false)}>{t("day.move")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default function DayBoard() {
  const { date: param } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { positions, label } = usePositions();
  const date = param || format(new Date(), "yyyy-MM-dd");
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(null);
  const load = useCallback(() => api.get("/day-board", { params: { date } }).then((r) => setData(r.data)), [date]);
  const flow = useAssignFlow(load);
  useEffect(() => { setData(null); load(); }, [load]);

  const go = (d) => d && navigate(`/admin/day/${d}`);
  const assign = (ev, slot, p) => flow.run((f) => api.post("/assignments", { event_id: ev.id, slot_id: slot.id, employee_id: p.id, ...f }), t("b.assignedOk"), p.name);
  const transfer = (item, ev, slot, keep) => flow.run((f) => api.post(`/assignments/${item.id}/transfer`, { event_id: ev.id, slot_id: slot.id, keep, ...f }), t("b.movedOk"), item.name);
  const onDrop = (ev, slot, item) => {
    if (item.slot_id === slot.id) return;
    if (item.event_id === ev.id) transfer(item, ev, slot, false);
    else setPending({ item, ev, slot });
  };
  const remove = async (a) => {
    try { await api.delete(`/assignments/${a.id}`); toast.success(t("b.removedOk")); load(); } catch (e) { toast.error(errorText(t, e)); }
  };
  const order = positions.map((p) => p.key);
  const keys = data ? [...new Set(data.events.flatMap((e) => e.slots.map((s) => s.position_key)))].sort((a, b) => order.indexOf(a) - order.indexOf(b)) : [];

  return (
    <div data-testid="day-board-page">
      <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-black mb-6"><ArrowLeft className="w-3.5 h-3.5" />{t("nav.dashboard")}</Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="eyebrow mb-2">{t("day.title")}</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight capitalize" data-testid="day-title">{fmtDate(date, lang)}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="day-prev" onClick={() => go(format(addDays(parseISO(date), -1), "yyyy-MM-dd"))} className="press p-2 rounded-full border border-white/10 hover:border-[#C5A059]"><ChevronLeft className="w-4 h-4" /></button>
          <Input type="date" value={date} onChange={(e) => go(e.target.value)} className="h-9 w-40" data-testid="day-date-input" />
          <button data-testid="day-next" onClick={() => go(format(addDays(parseISO(date), 1), "yyyy-MM-dd"))} className="press p-2 rounded-full border border-white/10 hover:border-[#C5A059]"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      {!data ? <p className="text-sm text-neutral-400">{t("c.loading")}</p> : !data.events.length ? <p className="text-sm text-neutral-500" data-testid="day-empty">{t("day.empty")}</p> : (
        <div className="card-lux overflow-x-auto" data-testid="day-table-wrap">
          <table className="border-separate border-spacing-0 w-max min-w-full">
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-background text-left p-3 eyebrow border-b border-r border-white/[0.08] w-[110px] min-w-[110px] sm:w-[150px] sm:min-w-[150px]">{t("day.position")}</th>
                {data.events.map((ev) => <EventHead key={ev.id} ev={ev} reload={load} />)}
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k} data-testid={`day-row-${k}`}>
                  <th className="sticky left-0 z-10 bg-background text-left align-top p-3 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.12em] border-b border-r border-white/[0.06] w-[110px] sm:w-[150px]">{label(k)}</th>
                  {data.events.map((ev) => {
                    const slots = ev.slots.filter((s) => s.position_key === k);
                    return (
                      <td key={ev.id} className="align-top p-2 border-b border-white/[0.06] space-y-2">
                        {slots.length ? slots.map((s) => <SlotCell key={s.id} ev={ev} slot={s} staff={data.staff} onDrop={onDrop} onAssign={assign} onRemove={remove} reload={load} />)
                          : <span className="text-neutral-600 text-xs">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <MoveDialog pending={pending} onClose={() => setPending(null)} onChoose={(keep) => { const p = pending; setPending(null); transfer(p.item, p.ev, p.slot, keep); }} />
      {flow.dialog}
    </div>
  );
}
